import { Capacitor } from '@capacitor/core'
import {
  DEJAVOO_PAYMENT_TERMINAL_WHITELIST,
  matchDejavooPaymentTerminal,
  suggestWhitelistEntry
} from '@/config/dejavooPaymentTerminals'

/** @type {Promise<object>|null} */
let profilePromise = null

/** @type {{ loaded: boolean, supportsSoftPos: boolean, supportsDvPayLiteIntegrated: boolean, supportsNativePush: boolean, isDejavooPaymentTerminal: boolean, matchReason: string|null }} */
let cachedFlags = {
  loaded: false,
  supportsSoftPos: true,
  supportsDvPayLiteIntegrated: false,
  supportsNativePush: true,
  isDejavooPaymentTerminal: false,
  matchReason: null
}

/**
 * Sync flags populated after {@link getAndroidDeviceProfile} resolves (Android native).
 */
export function getCachedAndroidDeviceFlags () {
  return { ...cachedFlags }
}

export function setCachedAndroidDeviceFlagsFromProfile (profile) {
  cachedFlags = {
    loaded: true,
    supportsSoftPos: !!profile?.supportsSoftPos,
    supportsDvPayLiteIntegrated: !!profile?.supportsDvPayLiteIntegrated,
    supportsNativePush: !!profile?.supportsNativePush,
    isDejavooPaymentTerminal: !!profile?.isDejavooPaymentTerminal,
    matchReason: profile?.matchReason ?? null
  }
}

/**
 * @param {object|null|undefined} deviceInfo
 * @param {{ matched: boolean, reason: string }} match
 */
export function classifyAndroidDevice (deviceInfo, match = matchDejavooPaymentTerminal(deviceInfo)) {
  const isDejavooPaymentTerminal = match.matched === true
  const isConsumerAndroid = !isDejavooPaymentTerminal

  return {
    isDejavooPaymentTerminal,
    isConsumerAndroid,
    supportsSoftPos: isConsumerAndroid,
    supportsDvPayLiteIntegrated: isDejavooPaymentTerminal,
    supportsNativePush: isConsumerAndroid,
    matchReason: match.reason
  }
}

/**
 * Cached Android device profile for payment-path gating.
 *
 * @param {{ force?: boolean }} [options]
 */
export async function getAndroidDeviceProfile (options = {}) {
  if (options.force) {
    profilePromise = null
  }
  if (profilePromise) {
    return profilePromise
  }

  profilePromise = loadAndroidDeviceProfile()
  return profilePromise
}

export function clearAndroidDeviceProfileCache () {
  profilePromise = null
  cachedFlags = {
    loaded: false,
    supportsSoftPos: true,
    supportsDvPayLiteIntegrated: false,
    supportsNativePush: true,
    isDejavooPaymentTerminal: false,
    matchReason: null
  }
}

/**
 * @param {object|null|undefined} info
 */
export function isDejavooPaymentTerminal (info) {
  return matchDejavooPaymentTerminal(info).matched
}

async function loadAndroidDeviceProfile () {
  const platform = Capacitor.getPlatform?.() || 'web'
  const isNativeAndroid = Capacitor.isNativePlatform?.() && platform === 'android'

  if (!isNativeAndroid) {
    const profile = buildNeutralProfile(platform)
    setCachedAndroidDeviceFlagsFromProfile(profile)
    return profile
  }

  const { loadAndroidDeviceProfileNative } = await import('./androidDeviceProfile.native.js')
  return loadAndroidDeviceProfileNative()
}

function buildNeutralProfile (platform) {
  const match = { matched: false, entry: null, reason: 'not_android_native' }
  const classification = classifyAndroidDevice(null, match)

  return {
    platform,
    manufacturer: null,
    model: null,
    name: null,
    osVersion: null,
    deviceId: null,
    isNativeAndroid: false,
    isDejavooPaymentTerminal: classification.isDejavooPaymentTerminal,
    isConsumerAndroid: classification.isConsumerAndroid,
    supportsSoftPos: classification.supportsSoftPos,
    supportsDvPayLiteIntegrated: classification.supportsDvPayLiteIntegrated,
    supportsNativePush: classification.supportsNativePush,
    matchReason: classification.matchReason,
    whitelistEntry: match.entry,
    suggestedWhitelistEntry: suggestWhitelistEntry({ manufacturer: null, model: null, name: null }),
    whitelistSize: DEJAVOO_PAYMENT_TERMINAL_WHITELIST.length
  }
}

export default {
  getAndroidDeviceProfile,
  clearAndroidDeviceProfileCache,
  getCachedAndroidDeviceFlags,
  setCachedAndroidDeviceFlagsFromProfile,
  isDejavooPaymentTerminal,
  classifyAndroidDevice,
  matchDejavooPaymentTerminal,
  suggestWhitelistEntry
}
